/** One rep's plan, or several stacked and collapsible.
 *
 *  A manager who filters to four reps wants four weeks, not the first one alphabetically.
 *  Each rep is a section that opens on click.
 *
 *  Collapsed sections are NOT mounted, and that is load-bearing rather than a lazy-loading
 *  nicety: reading `/plan/current` FREEZES that rep's week. Mounting every selected rep
 *  would make a manager's browsing decide, permanently, what twenty reps' plans of record
 *  are. So a section fetches only when someone asks to see it.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './SalesPlanBoard.css';
import { SalesPlanView } from './SalesPlanView';
import { SalesPlanEmailSettings } from './SalesPlanEmailSettings';
import { siftApiClient } from '../../api/siftApiClient';
import { describeError } from './salesPlanErrors';
import type { SalesPlanViewProps } from './SalesPlanView';
import type { PlannedStop } from '../../api/types';

export interface SalesPlanBoardProps extends Omit<SalesPlanViewProps, 'rep'> {
  /** From the CSE Rep filter. Still the only place a rep is chosen. */
  reps: string[];
}

export const SalesPlanBoard: React.FC<SalesPlanBoardProps> = ({
  reps, onStopsChange, ...panelProps
}) => {
  // The first rep is open so the common case -- one rep, or a manager checking the top of
  // their list -- costs no clicks. The rest wait to be asked.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(reps.slice(0, 1)));
  const [stopsByRep, setStopsByRep] = useState<Record<string, PlannedStop[]>>({});
  const [sending, setSending] = useState<'weekly' | 'digest' | null>(null);
  const [sendResult, setSendResult] = useState<string | null>(null);

  const toggle = useCallback((rep: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(rep)) next.delete(rep);
      else next.add(rep);
      return next;
    });
    // A closed section's stops must leave the map with it, or the route outlives the plan.
    setStopsByRep((prev) => {
      if (!(rep in prev)) return prev;
      const next = { ...prev };
      delete next[rep];
      return next;
    });
  }, []);

  const repKey = reps.join('\u0000');
  /* One STABLE callback per rep. The panel has `onStopsChange` in an effect dependency, so
   * an inline arrow here re-fires it on every render -- and since publishing sets state,
   * that is an infinite loop rather than a wasted render. */
  const publishers = useMemo(() => {
    const made: Record<string, (stops: PlannedStop[]) => void> = {};
    for (const rep of repKey ? repKey.split('\u0000') : []) {
      made[rep] = (stops) => setStopsByRep(
        (prev) => (prev[rep] === stops ? prev : { ...prev, [rep]: stops }),
      );
    }
    return made;
  }, [repKey]);

  const allStops = useMemo(
    // Ordered by the rep list rather than by arrival, so the map does not reshuffle
    // itself depending on which request came back first. Gated on `open` because a closing
    // panel's last publish can land after toggle() cleared its stops.
    () => reps.filter((r) => open.has(r)).flatMap((r) => stopsByRep[r] ?? []),
    [reps, open, stopsByRep],
  );

  useEffect(() => { onStopsChange?.(allStops); }, [allStops, onStopsChange]);

  /* `open` is seeded once at mount, so changing the rep filter left every section shut --
   * a page of headers and no plan. Drop reps that have gone, and if that leaves nothing
   * open, show the top of the new list. */
  useEffect(() => {
    setOpen((prev) => {
      const kept = new Set([...prev].filter((r) => reps.includes(r)));
      return kept.size ? kept : new Set(reps.slice(0, 1));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repKey]);

  const emailSummary = useCallback(async (kind: 'weekly' | 'digest') => {
    setSending(kind);
    setSendResult(null);
    try {
      const out = await siftApiClient.emailSalesPlanSummary(
        { reps, kind },
        { impersonate_role: panelProps.impersonateRole },
      );
      setSendResult(out.sent
        ? `Summary of ${out.reps} reps sent to ${out.recipient}.`
        : `Not sent: ${out.blocked_by ?? out.reason ?? 'unknown reason'}.`);
    } catch (err) {
      setSendResult(describeError(err, 'Could not send the summary.'));
    } finally {
      setSending(null);
    }
  }, [reps, panelProps.impersonateRole]);

  if (reps.length <= 1) {
    return (
      <div className="spb-shell">
        <SalesPlanEmailSettings
          impersonateRole={panelProps.impersonateRole}
          repOptions={panelProps.repOptions}
        />
        <SalesPlanView {...panelProps} rep={reps[0]} onStopsChange={onStopsChange} />
      </div>
    );
  }

  return (
    <div className="spb-shell">
      {/* OUTSIDE the scroller below, for two reasons: its rep dropdown is absolutely
          positioned and `overflow` on a scroll container clips it, and these settings
          should stay put while the plans move. */}
      <SalesPlanEmailSettings
        impersonateRole={panelProps.impersonateRole}
        repOptions={panelProps.repOptions}
      />

      {/* The per-rep buttons each send ONE rep's full route sheet. Across several reps that
          is several mails and none of them answers "how does the group look". */}
      <div className="spb-bulk">
        <span className="spb-bulk-label">All {reps.length} reps</span>
        <button
          type="button"
          className="spb-bulk-btn"
          disabled={sending !== null}
          onClick={() => emailSummary('weekly')}
        >
          {sending === 'weekly' ? 'Sending…' : 'Email me a summary'}
        </button>
        <button
          type="button"
          className="spb-bulk-btn"
          disabled={sending !== null}
          onClick={() => emailSummary('digest')}
        >
          {sending === 'digest' ? 'Sending…' : 'Email me a group update'}
        </button>
      </div>
      {sendResult ? <div className="spb-bulk-result">{sendResult}</div> : null}

      <div className="spb-root">
        {reps.map((rep) => {
        const isOpen = open.has(rep);
        const count = stopsByRep[rep]?.length;
        return (
          <section className="spb-rep" key={rep}>
            <button
              type="button"
              className="spb-head"
              aria-expanded={isOpen}
              onClick={() => toggle(rep)}
            >
              <span className={`spb-caret${isOpen ? ' spb-caret-open' : ''}`}>▸</span>
              <span className="spb-name">{rep}</span>
              {isOpen && count != null ? (
                <span className="spb-count">{count} stops</span>
              ) : (
                <span className="spb-hint">{isOpen ? 'Loading…' : 'Show plan'}</span>
              )}
            </button>
            {isOpen ? (
              <div className="spb-body">
                <SalesPlanView
                  {...panelProps}
                  rep={rep}
                  onStopsChange={publishers[rep]}
                />
              </div>
            ) : null}
          </section>
        );
      })}
      </div>
    </div>
  );
};
